import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type User } from "../lib/api";
import Spinner from "./Spinner";
import Avatar from "./Avatar";

const BUILD_TIME: string = (import.meta as any).env?.VITE_BUILD_TIME ?? "dev";

function formatDeployed(): string {
  if (BUILD_TIME === "dev") return "dev build";
  const d = new Date(BUILD_TIME);
  if (isNaN(d.getTime())) return BUILD_TIME;
  return `Deployed ${d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  })}`;
}

export default function UserMenu({ user, compact = false }: { user: User; compact?: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData(["me"], { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      nav("/login");
    },
  });

  const invitesQ = useQuery({ queryKey: ["my-invites"], queryFn: () => api.myInvites() });
  const pendingCount = invitesQ.data?.invites?.length ?? 0;

  const avatarSrc = user.avatarUpdatedAt
    ? api.avatars.userUrl(user.id, user.avatarUpdatedAt)
    : null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className={
          "relative flex items-center rounded-md hover:bg-surface-2 " +
          (compact ? "justify-center p-1 mx-auto" : "gap-2 w-full px-2 py-1.5")
        }
        aria-haspopup="menu"
        aria-expanded={open}
        title={compact ? (user.name ?? user.email) : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar src={avatarSrc} name={user.name} email={user.email} size={compact ? 28 : 32} />
        {pendingCount > 0 && (
          <span
            className={
              "absolute h-4 min-w-[16px] px-1 rounded-full bg-warn text-[10px] font-semibold grid place-items-center text-black " +
              (compact ? "top-0 right-0" : "top-0 left-7")
            }
            aria-label={`${pendingCount} pending invitation${pendingCount === 1 ? "" : "s"}`}
          >
            {pendingCount}
          </span>
        )}
        {!compact && (
          <>
            <span className="flex-1 min-w-0 text-left text-sm text-text truncate">{user.name ?? user.email}</span>
            <ChevronDown />
          </>
        )}
      </button>

      {open && (
        <div role="menu" className="absolute bottom-full mb-1 left-0 w-60 card shadow-[var(--shadow-strong)] p-1 z-50 text-sm">
          <div className="px-3 py-2 border-b border-border">
            <div className="font-medium truncate text-text">{user.name ?? "Signed in"}</div>
            <div className="text-xs muted-strong truncate">{user.email}</div>
          </div>
          <button
            role="menuitem"
            type="button"
            onClick={() => { setOpen(false); nav("/account"); }}
            className="w-full flex items-center gap-2 rounded px-3 py-2 hover:bg-surface-2 text-left text-text"
          >
            <UserIcon />
            <span className="flex-1">Account</span>
            {pendingCount > 0 && (
              <span className="chip bg-warn text-black">{pendingCount} invite{pendingCount === 1 ? "" : "s"}</span>
            )}
          </button>
          <div className="border-t border-border my-1" />
          <button
            role="menuitem"
            type="button"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className="w-full flex items-center gap-2 rounded px-3 py-2 hover:bg-surface-2 disabled:opacity-60 text-left text-text"
          >
            {logout.isPending ? <Spinner /> : <LogoutIcon />}
            <span className="flex-1">{logout.isPending ? "Signing out…" : "Sign out"}</span>
          </button>
          <div className="border-t border-border mt-1 pt-2 pb-1 px-3 text-[10px] muted-strong">
            {formatDeployed()}
          </div>
        </div>
      )}
    </div>
  );
}

function ChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M16 17l5-5-5-5M21 12H9M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
